<template>
  <InfoCard 
    :showApplyToVariants="true" 
    :isSaving="isSaving"
    :showSuccessState="showSuccessState"
    :isSaved="showSuccessState || !isSaved"
    :saveButtonWidth="showSuccessState ? 'auto' : '183px'"
    checkboxId="nutrition-rate-apply-variants"
    v-model:applyToVariants="applyToAllVariants"
    @saveChanges="handleSaveChanges"
  >
    <div class="nutrition-values">
      <div class="title-area">
        <div class="title">Ürün Besin Değerleri</div>
        <div class="description">
          Ürün besin değerleri, ürünün içeriği ve içerik miktarı hakkında
          bilgilendirme sağlar. Gireceğiniz değerler 100 gr veya ml içindir.
        </div>
      </div>

      <div class="nutrition-values-content" :class="{ disabled: isProductDisabled }">
        <div class="nutrition-selector">
          <DropdownSelectable
            ref="dropdownRef"
            label="Besin Değerleri Seçin"
            :dropItems="availableNutritionOptions"
            @selectedItems="handleSelectedItems"
            :disabled="isProductDisabled"
          />
        </div>

        <div class="nutrition-fields">
          <div
            v-for="(item, index) in nutritionList"
            :key="item.code"
            class="nutrition-input"
          >
            <Input
              type="text"
              inputmode="decimal"
              :value="nutritionList[index].value"
              @input="(event) => handleNutritionInput(event, index)"
              @keyup="(event) => handleNutritionInput(event, index)"
              :id="item.key"
              :label="item.label"
              :disabled="isProductDisabled"
            />
          </div>
        </div>
      </div>
    </div>
  </InfoCard>
</template>

<script setup>
import { ref, watch, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { Input, DropdownSelectable } from "@n11/so-ui-components";
import InfoCard from "../ProductSafetyComponent/InfoCard.vue";

const props = defineProps({
  nutritionValuesRate: {
    type: Object,
    default: () => ({
      selectedNutritionCodes: [],
      nutritionValues: []
    }),
  },
  isSaved: {
    type: Boolean,
    default: false
  },
  onSaveChanges: {
      type: Function,
      default: null,
  },
});

const store = useStore();
const isProductDisabled = computed(() => store.getters['products/isProductDisabled']);

const nutritionList = ref([]);
const selectedCodes = ref([]);
const dropdownRef = ref(null);
const allNutritionOptions = ref([]);
const applyToAllVariants = ref(true);
const isInitialized = ref(false);
const isSaving = ref(false);
const showSuccessState = ref(false);
const emit = defineEmits(['hasChanges', 'saveChanges']);

onMounted(async () => {
  try {
    const response = await store.dispatch("products/getProductNutritionOptions");
    
    if (response && response["nutritional-values"] && Array.isArray(response["nutritional-values"])) {
      allNutritionOptions.value = response["nutritional-values"].map(item => ({
        id: item.value,
        name: item.label,
        value: item.value
      }));
    }
  } catch (error) {
    console.error("Failed to fetch nutrition options:", error);
    allNutritionOptions.value = [];
  }
});

const availableNutritionOptions = computed(() => {
  return allNutritionOptions.value;
});

const handleSelectedItems = (items) => {
  selectedCodes.value = items.map(item => item.value);
};

watch([() => props.nutritionValuesRate, allNutritionOptions], ([data, options]) => {
  
  if (data?.selectedNutritionCodes && Array.isArray(data.selectedNutritionCodes) && options.length > 0) {
    selectedCodes.value = data.selectedNutritionCodes.map(code => code.value);

    if (dropdownRef.value) {
      const preSelectedItems = data.selectedNutritionCodes
        .map(code => {
          return options.find(option => option.value === code.value);
        })
        .filter(Boolean);
      
      setTimeout(() => {
        if (dropdownRef.value && preSelectedItems.length > 0) {
          dropdownRef.value.selectedItems = preSelectedItems;
        }
        isInitialized.value = true;
      }, 150);
    } else {
      isInitialized.value = true;
    }
  } else if (options.length > 0) {
    isInitialized.value = true;
  }
}, { immediate: true, deep: true });

watch(selectedCodes, (newCodes, oldCodes) => {
  if (!newCodes || !Array.isArray(newCodes) || newCodes.length === 0) {
    nutritionList.value = [];
    if (isInitialized.value && JSON.stringify(newCodes) !== JSON.stringify(oldCodes)) {
      emit('hasChanges');
    }
    return;
  }
  
  if (isInitialized.value && JSON.stringify(newCodes) !== JSON.stringify(oldCodes)) {
    emit('hasChanges');
  }
  
  nutritionList.value = newCodes.map(code => {
    const optionFromApi = allNutritionOptions.value.find(opt => opt.value === code);
    const label = optionFromApi?.name || `Nutrition ${code}`;
    
    const apiValue = props.nutritionValuesRate?.nutritionValues?.find(v => 
      v.nutritionCode?.value == code
    );
    
    return {
      id: apiValue?.id || null,
      code,
      key: label.toLowerCase().replace(/[()]/g, '').replace(/\s+/g, '_'),
      label: label,
      value: apiValue?.nutritionValue || ""
    };
  });
}, { immediate: true, deep: true });

watch(
  [() => nutritionList.value.map(item => item.value), applyToAllVariants],
  ([newValues, newApplyToAll], [oldValues, oldApplyToAll]) => {
    if (isInitialized.value) {
      const valuesChanged = oldValues !== undefined && JSON.stringify(newValues) !== JSON.stringify(oldValues);
      const applyChanged = oldApplyToAll !== undefined && newApplyToAll !== oldApplyToAll;
      
      if (valuesChanged || applyChanged) {
        emit('hasChanges');
      }
    }
  },
  { deep: true }
);

const getData = () => {
  return {
    applyToAllVariants: applyToAllVariants.value,
    selectedNutritionCodes: selectedCodes.value.map(code => {
      const optionFromApi = allNutritionOptions.value.find(opt => opt.value === code);
      const label = optionFromApi?.name || `Nutrition ${code}`;
      
      return {
        value: code,
        label: label,
        code: null
      };
    }),
    nutritionValues: nutritionList.value
      .filter(item => item.value !== "" && item.value !== null)
      .map(item => ({
        id: item.id,
        nutritionCode: {
          value: item.code,
          label: item.label,
          code: null
        },
        nutritionValue: item.value
      }))
  };
};
const resetSuccessState = () => {
  showSuccessState.value = false;
};

const showSaveAllSuccessState = () => {
  showSuccessState.value = true;
};

const sanitizeNutritionValue = (value) => {
  const raw = String(value ?? "");
  const normalized = raw.replace(/\./g, ",").replace(/[^0-9,]/g, "");

  const firstCommaIndex = normalized.indexOf(",");
  if (firstCommaIndex === -1) return normalized;

  const before = normalized.slice(0, firstCommaIndex + 1);
  const after = normalized.slice(firstCommaIndex + 1).replace(/,/g, "");
  return before + after;
};

const handleNutritionInput = (event, index) => {
  const incoming = event?.target?.value ?? "";
  const cleaned = sanitizeNutritionValue(incoming);

  if (nutritionList.value?.[index]) {
    nutritionList.value[index].value = cleaned;
  }
};

const handleSaveChanges = async () => {
  if (!props.onSaveChanges) return;

  try {
    isSaving.value = true;

    await props.onSaveChanges();

    showSuccessState.value = true;

    store.commit(
      "common/SET_TOASTER_DATA",
      {
        type: "success",
        title: "Başarılı",
        description: "Yaptığınız değişiklikler kaydedilmiştir.",
      }
    );
  } catch (error) {
    showSuccessState.value = false;

    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      "İşlem sırasında bir hata oluştu. Lütfen tekrar deneyin.";

    store.commit(
      "common/SET_TOASTER_DATA",
      {
        type: "error",
        title: "Hata",
        description: errorMessage,
      }
    );

    throw error;
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ getData, resetSuccessState, showSaveAllSuccessState });
</script>

<style scoped lang="scss">
@import "@/assets/styles/variables.scss";

.nutrition-values {
  display: flex;
  flex-direction: column;
  margin: 16px 0;

  .title-area {
    .title {
      font-size: 14px;
      font-weight: 600;
      line-height: 18px;
      letter-spacing: -0.05px;
      margin-bottom: 8px;
      color: $colorN80;
    }

    .description {
      font-size: 11px;
      line-height: 14px;
      height: 30px;
      color: $colorN80;
    }
  }

  .nutrition-values-content {
    display: flex;
    gap: 12px;

    &.disabled {
      pointer-events: none;

      :deep(input),
      :deep(.input-wrapper),
      :deep(.input-container) {
        pointer-events: none;
      }
      :deep(.dropdownSelectable) {
        pointer-events: none;
        
        .dropdownSelectable-btn {
          background-color: #f6f7f9;
        }
        .dropdownSelectable-btn-content {
          opacity: 0.5;
        }
      }
    }
  }

  .nutrition-selector {
    flex: 1;
  }

  .nutrition-fields {
    flex: 1;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;

    .nutrition-input {
      display: flex;
      flex-direction: column;
      position: relative;

      label {
        font-size: 13px;
        font-weight: 500;
        margin-bottom: 4px;
        color: $dark-gray-5;
      }

      input {
        border: 1px solid $colorN30;
        border-radius: 6px;
        padding: 8px 30px 8px 10px;
        font-size: 13px;
        color: $dark-gray-5;

        &:focus {
          outline: none;
          border-color: $colorM50;
        }
      }

      .unit {
        position: absolute;
        right: 10px;
        bottom: 8px;
        font-size: 12px;
        color: $colorN60;
      }
    }
  }
}

</style>
